import AccountProfileResource from 'App/resources/AccountProfile';
import InkyParserResource from 'Resources/InkyParser';
import AccountProfile from 'App/resources/Model/AccountProfile';
import MailCampaign from 'App/resources/Model/MailCampaign';
import MailTemplate from 'App/resources/Model/MailTemplate';
import logError from 'Helpers/logError';
import openModal from 'App/services/Modal';
import AccountProfileTemplate from 'App/babel/resources/AccountProfileTemplate';

export type PreviewData = { accountProfile: AccountProfile; style: string } | null;

export const generateHTML = async (bodyJson: string, style: string, accountProfile: AccountProfile) => {
	const { data } = await InkyParserResource.parse(bodyJson, {
		extraCss: style,
		profile: accountProfile
	});
	return data;
};

export const getPreviewData = async (): Promise<PreviewData> => {
	try {
		const [{ data: accountProfile }, { data: style }] = await Promise.all([
			AccountProfileResource.get(),
			InkyParserResource.getCss(false)
		]);

		if (!accountProfile) {
			return null;
		}
		return { accountProfile, style: style || '' };
	} catch (e) {
		logError(e, 'Failed to get preview data');
		return null;
	}
};

export const getStyleDataAndGenerateHTML = async (bodyJson: string) => {
	try {
		const previewData = await getPreviewData();
		if (previewData === null) {
			return '';
		}
		const { style, accountProfile } = previewData;
		const html = generateHTML(bodyJson, style, accountProfile);
		return html;
	} catch (e) {
		logError(e, 'Failed to get preview data');
		return '';
	}
};

export async function previewTemplate(
	event: React.MouseEvent,
	template: MailTemplate | MailCampaign,
	accountProfile: AccountProfile,
	style: string,
	isUpsalesTemplate?: boolean
) {
	event?.stopPropagation();

	let html: string = '';
	if (template.bodyJson && template.version === 1) {
		try {
			html = await generateHTML(template.bodyJson, style, accountProfile);
		} catch (e) {
			logError(e, 'Failed to parse template bodyJson');
		}
	} else if (template.version === 2 && isUpsalesTemplate) {
		try {
			const result = await AccountProfileTemplate.apply(template, accountProfile, {
				processBody: true,
				processBodyJson: true,
				isStandardTemplate: true
			});
			const processedTemplate = result.data || result;
			html = processedTemplate.body || template.body;
		} catch (e) {
			logError(e, 'Failed to apply account profile to Unlayer standard template');
			html = template.body;
		}
	} else if (template.version === 2) {
		html = template.body;
		try {
			const bodyJson = typeof template.bodyJson === 'string' ? JSON.parse(template.bodyJson) : template.bodyJson;
			const linkColor = bodyJson?.body?.values?.linkStyle?.linkColor || accountProfile?.colors?.linkText;
			if (linkColor && html) {
				html = html.replace(/<a\b([^>]*?)style="([^"]*)"([^>]*?)>/g, `<a$1style="color: ${linkColor}; $2"$3>`);
				html = html.replace(/<a\b(?![^>]*style=)([^>]*?)>/g, `<a style="color: ${linkColor};"$1>`);
			}
		} catch {
			// Use raw body if bodyJson parsing fails
		}
	} else {
		html = template.body;
	}

	openModal('MailPreview', {
		name: template.name,
		html,
		isPlainText: !template.bodyJson
	});
}
